import React, { useState, useEffect } from "react";
import { Pencil, Trash2, Loader, Plus, X, Layers, ListPlus } from "lucide-react";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";
import SearchableSelect from "./SearchableSelect";
import ConfirmationModal from "./ConfirmationModal";

export default function DataAddonTable({ searchTerm }) {
  const { showSuccess, showError } = useNotification();
  const [subTab, setSubTab] = useState("addons"); // addons | groups

  // Data states
  const [addons, setAddons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination states
  const [page, setPage] = useState(() => {
    try {
      const saved = sessionStorage.getItem("masterDataAddonPagination");
      return saved ? JSON.parse(saved).page || 1 : 1;
    } catch {
      return 1;
    }
  });
  const [limit, setLimit] = useState(() => {
    try {
      const saved = sessionStorage.getItem("masterDataAddonPagination");
      return saved ? JSON.parse(saved).limit || 10 : 10;
    } catch {
      return 10;
    }
  });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    try {
      sessionStorage.setItem("masterDataAddonPagination", JSON.stringify({ page, limit }));
    } catch {}
  }, [page, limit]);

  // Modal states
  const [isAddonModalOpen, setIsAddonModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Modal forms
  const [addonName, setAddonName] = useState("");
  const [purchasePrice, setPurchasePrice] = useState(0);
  const [sellPrice, setSellPrice] = useState(0);

  const [groupName, setGroupName] = useState("");
  const [groupDesc, setGroupDesc] = useState("");
  const [minSelection, setMinSelection] = useState(0);
  const [maxSelection, setMaxSelection] = useState(1);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);

  const [saving, setSaving] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "danger",
    confirmText: "Hapus",
    onConfirm: () => {},
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch function
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (subTab === "addons") {
        const res = await masterService.addons.list({
          query: searchTerm,
          page,
          limit,
        });
        setAddons(res?.data || []);
        setTotal(res?.meta?.total || 0);
      } else {
        const res = await masterService.addonGroups.list({
          query: searchTerm,
          page,
          limit,
        });
        setGroups(res?.data || []);
        setTotal(res?.meta?.total || 0);
      }
    } catch (err) {
      console.error("Error fetching addons:", err);
      setError(err.message || "Gagal mengambil data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [subTab, page, limit, searchTerm]);

  // Handle Addon Modal open
  const openAddonModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      setAddonName(item.name || "");
      setPurchasePrice(item.purchase_price || 0);
      setSellPrice(item.sell_price || 0);
    } else {
      setAddonName("");
      setPurchasePrice(0);
      setSellPrice(0);
    }
    setIsAddonModalOpen(true);
  };

  // Save Addon
  const handleSaveAddon = async () => {
    if (!addonName.trim()) {
      showError("Nama Pilihan Extra harus diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: addonName.trim(),
        purchase_price: parseInt(purchasePrice) || 0,
        sell_price: parseInt(sellPrice) || 0,
      };

      if (editingItem) {
        await masterService.addons.update(editingItem.id, payload);
        showSuccess("Pilihan Extra berhasil diperbarui");
      } else {
        await masterService.addons.create(payload);
        showSuccess("Pilihan Extra berhasil ditambahkan");
      }
      setIsAddonModalOpen(false);
      fetchData();
    } catch (err) {
      showError(err.message || "Gagal menyimpan data");
    } finally {
      setSaving(false);
    }
  };

  // Delete Addon
  const handleDeleteAddon = (item) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Pilihan Extra?",
      message: `Apakah Anda yakin ingin menghapus pilihan "${item.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        setDeleteLoading(true);
        try {
          await masterService.addons.delete(item.id);
          showSuccess("Pilihan Extra berhasil dihapus");
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          fetchData();
        } catch (err) {
          showError(err.message || "Gagal menghapus data");
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  // Handle Group Modal open
  const openGroupModal = async (item = null) => {
    setEditingItem(item);
    setSaving(true);

    // Fetch all addons to show in checkboxes
    let allAddons = [];
    try {
      const res = await masterService.addons.list({ limit: 200 });
      allAddons = res?.data || [];
    } catch (err) {
      console.error(err);
    }

    if (item) {
      setGroupName(item.name || "");
      setGroupDesc(item.description || "");
      setMinSelection(item.min_selection || 0);
      setMaxSelection(item.max_selection || 1);
      setSelectedAddonIds((item.addons || []).map((a) => a.id));
    } else {
      setGroupName("");
      setGroupDesc("");
      setMinSelection(0);
      setMaxSelection(1);
      setSelectedAddonIds([]);
    }

    setSaving(false);
    setIsGroupModalOpen(true);
  };

  // Save Group
  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      showError("Nama grup harus diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: groupName.trim(),
        description: groupDesc.trim() || null,
        min_selection: parseInt(minSelection) || 0,
        max_selection: parseInt(maxSelection) || 1,
        addon_ids: selectedAddonIds,
      };

      if (editingItem) {
        await masterService.addonGroups.update(editingItem.id, payload);
        showSuccess("Grup Pilihan Extra berhasil diperbarui");
      } else {
        await masterService.addonGroups.create(payload);
        showSuccess("Grup Pilihan Extra berhasil ditambahkan");
      }
      setIsGroupModalOpen(false);
      fetchData();
    } catch (err) {
      showError(err.message || "Gagal menyimpan grup");
    } finally {
      setSaving(false);
    }
  };

  // Delete Group
  const handleDeleteGroup = (item) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Grup Pilihan Extra?",
      message: `Apakah Anda yakin ingin menghapus grup "${item.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        setDeleteLoading(true);
        try {
          await masterService.addonGroups.delete(item.id);
          showSuccess("Grup Pilihan Extra berhasil dihapus");
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          fetchData();
        } catch (err) {
          showError(err.message || "Gagal menghapus grup");
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  // Checkbox handler
  const handleToggleAddon = (id) => {
    setSelectedAddonIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const startEntry = (page - 1) * limit + 1;
  const endEntry = Math.min(page * limit, total);
  const maxPages = Math.ceil(total / limit) || 1;

  // Render list checklist addons
  const [allAddonsList, setAllAddonsList] = useState([]);
  useEffect(() => {
    if (isGroupModalOpen) {
      masterService.addons
        .list({ limit: 200 })
        .then((res) => setAllAddonsList(res?.data || []))
        .catch(() => {});
    }
  }, [isGroupModalOpen]);

  return (
    <div className="space-y-6">
      {/* Sub-tabs selector */}
      <div className="flex items-center justify-between">
        <div className="flex bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => {
              setSubTab("addons");
              setPage(1);
            }}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
              subTab === "addons"
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <ListPlus className="w-3.5 h-3.5" />
            Pilihan Extra (Addons)
          </button>
          <button
            onClick={() => {
              setSubTab("groups");
              setPage(1);
            }}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
              subTab === "groups"
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            Grup Pilihan Extra
          </button>
        </div>

        {/* Action Button */}
        <button
          onClick={() => (subTab === "addons" ? openAddonModal() : openGroupModal())}
          className="flex items-center gap-1.5 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium text-xs shadow-sm shadow-teal-600/10 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {subTab === "addons" ? "Tambah Pilihan Extra" : "Tambah Grup Pilihan"}
        </button>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm overflow-hidden">
        {loading && (
          <div className="px-6 py-12 flex items-center justify-center text-slate-500">
            <Loader className="w-5 h-5 animate-spin mr-2" />
            Memuat data...
          </div>
        )}

        {error && !loading && (
          <div className="px-6 py-4 bg-rose-50 border-b border-rose-200 text-rose-700 text-sm">
            Gagal: {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">
                      Aksi
                    </th>
                    {subTab === "addons" ? (
                      <>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Nama Pilihan
                        </th>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Harga Modal
                        </th>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Harga Jual
                        </th>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Stok
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Nama Grup
                        </th>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Deskripsi
                        </th>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Min/Max Pilihan
                        </th>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Daftar Addons
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {subTab === "addons" ? (
                    addons.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-slate-400 text-sm">
                          Tidak ada data
                        </td>
                      </tr>
                    ) : (
                      addons.map((item, index) => (
                        <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => openAddonModal(item)}
                                className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteAddon(item)}
                                className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                            {item.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            Rp {item.purchase_price.toLocaleString("id-ID")}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600 font-semibold">
                            Rp {item.sell_price.toLocaleString("id-ID")}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                              item.stok > 0 ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"
                            }`}>
                              {item.stok > 0 ? item.stok : "—"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )
                  ) : groups.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-slate-400 text-sm">
                        Tidak ada data
                      </td>
                    </tr>
                  ) : (
                    groups.map((item, index) => (
                      <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => openGroupModal(item)}
                              className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteGroup(item)}
                              className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {item.description || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          Min: {item.min_selection} / Max: {item.max_selection}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <div className="flex flex-wrap gap-1 max-w-sm">
                            {(item.addons || []).map((a) => (
                              <span
                                key={a.id}
                                className="inline-flex px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600 font-medium border border-slate-200"
                              >
                                {a.name}
                              </span>
                            ))}
                            {(item.addons || []).length === 0 && (
                              <span className="text-slate-400 italic">Belum ada pilihan</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  Showing <span className="font-medium text-slate-700">{startEntry}</span> to{" "}
                  <span className="font-medium text-slate-700">{endEntry}</span> of{" "}
                  <span className="font-medium text-slate-700">{total}</span> entries
                </span>
                <SearchableSelect
                  options={[
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                    { value: 50, label: "50" },
                    { value: 100, label: "100" },
                  ]}
                  value={limit}
                  onChange={(val) => {
                    setLimit(val);
                    setPage(1);
                  }}
                  placeholder="Items"
                  displayKey="label"
                  valueKey="value"
                  className="w-20"
                  searchable={false}
                  size="sm"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ‹‹
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ‹
                </button>
                <span className="text-xs text-slate-500 px-2">
                  Page <span className="font-semibold text-slate-700">{page}</span> of{" "}
                  <span className="font-semibold text-slate-700">{maxPages}</span>
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(maxPages, p + 1))}
                  disabled={page === maxPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ›
                </button>
                <button
                  onClick={() => setPage(maxPages)}
                  disabled={page === maxPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ››
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Addon modal dialog */}
      {isAddonModalOpen && (
        <div
          onClick={() => setIsAddonModalOpen(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-md flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          >
            <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white">
              <h2 className="text-base font-semibold">
                {editingItem ? "Edit Pilihan Extra" : "Tambah Pilihan Extra"}
              </h2>
              <button
                onClick={() => setIsAddonModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors text-white/85"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Nama Pilihan Extra <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={addonName}
                  onChange={(e) => setAddonName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                  placeholder="Contoh: Tempe Goreng"
                  disabled={saving}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Harga Modal (Rp)
                  </label>
                  <input
                    type="number"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Harga Jual (Rp) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={sellPrice}
                    onChange={(e) => setSellPrice(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex justify-end gap-3">
              <button
                onClick={() => setIsAddonModalOpen(false)}
                disabled={saving}
                className="px-4 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleSaveAddon}
                disabled={saving}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm"
              >
                {saving ? "Menyimpan..." : "Simpan Pilihan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Addon Group modal dialog */}
      {isGroupModalOpen && (
        <div
          onClick={() => setIsGroupModalOpen(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-lg flex flex-col overflow-hidden max-h-[90vh] animate-in fade-in zoom-in-95 duration-200"
          >
            <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white">
              <h2 className="text-base font-semibold">
                {editingItem ? "Edit Grup Pilihan" : "Tambah Grup Pilihan"}
              </h2>
              <button
                onClick={() => setIsGroupModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors text-white/85"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Nama Grup Pilihan <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                  placeholder="Contoh: Pilihan Extra"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Deskripsi Grup (Opsional)
                </label>
                <textarea
                  value={groupDesc}
                  onChange={(e) => setGroupDesc(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors resize-none h-16"
                  placeholder="Contoh: Pilihan tambahan makanan sampingan"
                  disabled={saving}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Minimal Pilihan
                  </label>
                  <input
                    type="number"
                    value={minSelection}
                    onChange={(e) => setMinSelection(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Maksimal Pilihan
                  </label>
                  <input
                    type="number"
                    value={maxSelection}
                    onChange={(e) => setMaxSelection(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Checklist Addons */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">
                  Daftar Pilihan Extra yang Termasuk
                </label>
                <div className="border border-slate-200 rounded-xl bg-slate-50 p-4 max-h-48 overflow-y-auto space-y-2.5">
                  {allAddonsList.map((addon) => {
                    const isChecked = selectedAddonIds.includes(addon.id);
                    return (
                      <label
                        key={addon.id}
                        className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-100 rounded-lg shadow-sm cursor-pointer hover:bg-slate-50/50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleAddon(addon.id)}
                          className="w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                        />
                        <div className="flex-1 flex justify-between items-center text-sm">
                          <span className="font-medium text-slate-800">{addon.name}</span>
                          <span className="text-xs text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded">
                            +Rp {addon.sell_price.toLocaleString("id-ID")}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                  {allAddonsList.length === 0 && (
                    <div className="text-slate-400 text-xs text-center py-4 italic">
                      Belum ada pilihan extra dibuat. Silakan tambahkan pilihan extra terlebih dahulu.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex justify-end gap-3">
              <button
                onClick={() => setIsGroupModalOpen(false)}
                disabled={saving}
                className="px-4 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleSaveGroup}
                disabled={saving}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm"
              >
                {saving ? "Menyimpan..." : "Simpan Grup"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          setConfirmationModal({ ...confirmationModal, isOpen: false })
        }
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText={confirmationModal.confirmText}
        isLoading={deleteLoading}
      />
    </div>
  );
}
